import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import { ACCEPTED, describeImage, prepareSearchImage, searchByImage, takeSearchImage } from '../lib/marketImageSearch';
import { searchMarket } from '../lib/marketCatalog';
import { readFilters, SORTS, writeFilters } from '../lib/marketFilters';
import { SERVICE_ROUTES } from '../lib/marketServices';
import { attributeLabel, attributeValueLabel } from '../lib/marketAttributes';
import MarketShell, { Icon } from '../components/market/MarketShell';
import { SourceCard } from '../components/market/SourceResults';
import MarketProductCard from '../components/market/MarketProductCard';
import { ServicesPanel } from './MarketHome';
import { track } from '../lib/growthTracker';

// /market/image-search — search by photo across MAABAR's suppliers (HioBuy's
// sources, market-source-search). Results are visually similar products, not
// a "match": the page says so and shows no score. Nothing names a source —
// pictures come from market-image by opaque ref, titles in Arabic. Opening a
// result prepares it as a MAABAR product (imported on demand, the same
// screening and publication rules as the catalog); one that does not pass is
// "not available", with MAABAR's services offered instead. Visitors may search
// too (owner decision 2026-10-22), limited by address on the server.
//
// Our products first (owner, 2026-10-25: «البحث بمنتجاتنا اول بعدين يبحث من
// هيوباي»): beside the photo search the server reads the photo as a few words
// (describe) and the store's own catalog is searched with them, in a second or
// two, with the store's sorts and the private-label filter; the photo search's
// results join the same list when it ends. ONE list, no word for where a
// product comes from (owner, 2026-10-25: «ما ابي يشك العميل اننا لنا متجر
// ونجيب من مصدر خارجي») — the store's products first, or all of them in one
// price order under a price sort. Under the photo, an advert for quote requests (owner: «صفحة البحث
// بالصوره تكون اعلان لمعبر عروض الاسعار»). The app's MarketImageSearchScreen
// is the same page.
const OWN_LIMIT = 8;
const PL_VALUES = ['logo', 'custom'];

const LAST_KEY = 'maabar_photo_results_v1';
// A search in flight must never be covered by the previous results (React's
// development double mount restored them while the new search ran).
let searching = false;

const T = {
  ar: {
    market: 'السوق', title: 'البحث بالصورة', lead: 'ارفع صورة منتج، ونبحث لك عن منتجات مشابهة.',
    pick: 'اختر صورة', take: 'التقط صورة', drop: 'أو اسحب الصورة وأفلتها هنا', change: 'صورة أخرى', searching: 'نبحث عن منتجات مشابهة…',
    results: (n) => `${n} منتج مشابه في المظهر`, note: 'التشابه في الصورة لا يعني أنه المنتج نفسه — افتح المنتج وراجع المواصفات والخيارات قبل الطلب.',
    none: 'لم نجد منتجات مشابهة.', signIn: 'سجّل الدخول للبحث بالصورة', signInBtn: 'تسجيل الدخول', noneSub: 'نستطيع توفيره لك من المصانع مباشرة:',
    similar: 'منتجات مشابهة', seeAll: 'اعرض المزيد من المنتجات المشابهة',
    sorts: { relevance: 'الأنسب', price_asc: 'الأقل سعراً', price_desc: 'الأعلى سعراً', rating: 'الأعلى تقييماً', newest: 'الأحدث' },
    ad: ['اطلب عرض سعر من المصانع', 'اكتب طلبك وأرفق صورته، وتوصلك عروض أسعار المصانع مباشرة — مجاناً.'],
    yourPhoto: 'صورتك', formats: 'JPG أو PNG أو WEBP، حتى 15 ميجابايت',
    err: { type: 'الصيغ المدعومة: JPG وPNG وWEBP', size: 'حجم الصورة أكبر من 15 ميجابايت', unreadable: 'تعذّرت قراءة الصورة',
      rate_limited: 'انتظر قليلًا قبل البحث مرة أخرى.', daily_limit: 'وصلت إلى حد البحث بالصورة لليوم — جرّب غدًا.', busy: 'الخدمة مشغولة الآن — حاول بعد قليل.',
      provider_timeout: 'تأخر البحث — جرّب مرة ثانية، وما تنحسب عليك.',
      image_search_not_enabled: 'البحث بالصورة غير متاح حاليًا.', image_too_large: 'حجم الصورة كبير — اختر صورة أصغر.', failed: 'تعذّر البحث، حاول مرة أخرى.' },
  },
  en: {
    market: 'Market', title: 'Search by photo', lead: 'Upload a product photo and we look for similar products from our suppliers.',
    pick: 'Choose a photo', take: 'Take a photo', drop: 'or drag and drop it here', change: 'Another photo', searching: 'Looking for similar products…',
    results: (n) => `${n} visually similar products`, note: 'Looking alike does not mean it is the same product — open it and check the specifications and options before ordering.',
    none: 'No similar products found.', signIn: 'Sign in to search by photo', signInBtn: 'Sign in', noneSub: 'We can source it for you directly from factories:',
    similar: 'Similar products', seeAll: 'Show more similar products',
    sorts: { relevance: 'Best match', price_asc: 'Lowest price', price_desc: 'Highest price', rating: 'Top rated', newest: 'Newest' },
    ad: ['Get quotes from factories', 'Describe what you need and attach a photo — factory quotes come straight to you, free.'],
    yourPhoto: 'Your photo', formats: 'JPG, PNG or WEBP, up to 15 MB',
    err: { type: 'Supported formats: JPG, PNG, WEBP', size: 'The photo is larger than 15 MB', unreadable: 'The photo could not be read',
      rate_limited: 'Please wait a moment before searching again.', daily_limit: 'You have reached today\'s photo-search limit — try again tomorrow.', busy: 'The service is busy — try again shortly.',
      provider_timeout: 'The search took too long — try again; it does not count against you.',
      image_search_not_enabled: 'Search by photo is not available right now.', image_too_large: 'The photo is too large — choose a smaller one.', failed: 'The search failed — please try again.' },
  },
};
T.zh = T.en;

export default function MarketImageSearch({ lang = 'ar', user, exchangeRates }) {
  const nav = useNavigate();
  const c = T[lang] || T.ar;
  usePageTitle('market', lang);
  const [prep, setPrep] = useState(null);
  const [state, setState] = useState('idle');   // idle | searching | done | error
  const [results, setResults] = useState([]);
  const [err, setErr] = useState('');
  const [drag, setDrag] = useState(false);
  const pickRef = useRef(null), camRef = useRef(null);
  // «من متجرنا»: the words the photo was read as, and the store's products for them.
  // own.state: idle | loading | done | off (the server could not read the photo — only the photo search's results show)
  const [words, setWords] = useState(null);
  const [own, setOwn] = useState({ state: 'idle', rows: [], q: '', total: 0 });
  const [sort, setSort] = useState('relevance');
  const [pl, setPl] = useState([]);
  const runId = useRef(0);
  const keep = (patch) => {
    // Kept for this tab: Back from a product returns to the same results
    // (searching again would spend the customer's limit and HioBuy quota).
    try { sessionStorage.setItem(LAST_KEY, JSON.stringify({ ...JSON.parse(sessionStorage.getItem(LAST_KEY) || '{}'), ...patch })); } catch { /* storage full or blocked: Back then shows the upload screen */ }
  };

  const run = useCallback(async (p, source = 'searchbar') => {
    const id = ++runId.current;
    const started = Date.now();
    track('image_search_started', { source });
    setPrep(p); setState('searching'); setErr('');
    setWords(null); setOwn({ state: 'loading', rows: [], q: '', total: 0 });
    try { sessionStorage.removeItem(LAST_KEY); } catch { /* nothing kept */ }
    searching = true;
    // Both at once: the store's words come back in a second or two, the
    // suppliers' search takes 5–15 s. Neither waits for the other.
    describeImage(p).then((dsc) => {
      if (id !== runId.current) return;
      if (dsc.ok && (dsc.ar || dsc.en)) { const w = { ar: dsc.ar, en: dsc.en }; setWords(w); keep({ words: w }); }
      else setOwn({ state: dsc.ok ? 'done' : 'off', rows: [], q: '', total: 0 });
    });
    const r = await searchByImage(p).finally(() => { searching = false; });
    if (id !== runId.current) return;
    if (!r.ok && r.error === 'sign_in_required') { setState('signin'); return; }
    if (!r.ok) { setState('error'); setErr(c.err[r.error] || c.err.failed); return; }
    track('image_search_results_shown', { result_count: r.results.length, latency_ms: Date.now() - started });
    setResults(r.results); setState('done');
    keep({ dataUrl: p.dataUrl, results: r.results });
  }, [c]);

  // The store's products for the photo's words — again when the sort or the
  // private-label filter changes. Arabic words first, English if they find nothing.
  useEffect(() => {
    if (!words) return undefined;
    let alive = true;
    setOwn((o) => ({ ...o, state: 'loading' }));
    (async () => {
      const opts = { sort, attrs: pl.length ? { private_label: pl } : null, limit: OWN_LIMIT };
      let q = words.ar || words.en;
      let r = await searchMarket({ ...opts, q });
      if (!r.rows?.length && words.ar && words.en) { q = words.en; r = await searchMarket({ ...opts, q }); }
      if (alive) setOwn({ state: 'done', rows: (r.rows || []).slice(0, OWN_LIMIT), q, total: r.total || 0 });
    })();
    return () => { alive = false; };
  }, [words, sort, pl]);
  // One list: the store's products first, then the photo search's; under a
  // price sort, all of them in one price order.
  const ownItems = own.state === 'done' ? own.rows.map((p) => ({ key: p.id, own: p, price: Number(p.price_min_cny) })) : [];
  const srcItems = state === 'done' ? results.map((r) => ({ key: r.ref, src: r, price: Number(r.price_cny) })) : [];
  let items = [...ownItems, ...srcItems];
  if (sort === 'price_asc' || sort === 'price_desc') items = items.sort((a, b) => (a.price - b.price) * (sort === 'price_asc' ? 1 : -1));
  const busy = own.state === 'loading' || state === 'searching';
  const togglePl = (v) => setPl((x) => (x.includes(v) ? x.filter((k) => k !== v) : [...x, v]));
  // «عرض الكل»: the store list for the same words, sort and filter (its own URL format).
  const seeAll = () => nav(`/market/search?${writeFilters({ ...readFilters(new URLSearchParams()), q: own.q, sort, attrs: pl.length ? { private_label: pl } : {} })}`);
  useEffect(() => {
    const p = takeSearchImage();
    if (p) { run(p); return; }
    if (searching) return;
    try {
      const last = JSON.parse(sessionStorage.getItem(LAST_KEY) || 'null');
      if (last?.results) { setPrep({ dataUrl: last.dataUrl }); setResults(last.results); setState('done'); if (last.words) setWords(last.words); else setOwn({ state: 'off', rows: [], q: '', total: 0 }); }
    } catch { /* nothing kept */ }
  }, [run]);
  const onFile = async (file, source) => {
    if (!file) return;
    const p = await prepareSearchImage(file);
    if (p.error) { setErr(c.err[p.error]); setState('error'); return; }
    run(p, source);
  };
  const inputs = (
    <>
      <input ref={pickRef} type="file" accept={ACCEPTED.join(',')} hidden data-image-page-input="1" onChange={(e) => { onFile(e.target.files?.[0], 'gallery'); e.target.value = ''; }} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { onFile(e.target.files?.[0], 'camera'); e.target.value = ''; }} />
    </>
  );

  return (
    <MarketShell lang={lang} user={user}>
      <div className="mk-wrap" style={{ paddingBottom: 48 }}>
        <div className="mk-crumbs" aria-label="breadcrumb">
          <button type="button" onClick={() => nav('/market')}>{c.market}</button><span aria-hidden="true">›</span><span aria-current="page">{c.title}</span>
        </div>
        {inputs}
        {!prep ? (
          <div className={`mk-drop${drag ? ' on' : ''}`} data-image-drop="1"
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); onFile(e.dataTransfer.files?.[0], 'drop'); }}>
            <span className="mk-drop-icon"><Icon.Camera /></span>
            <h1>{c.title}</h1>
            <p>{c.lead}</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button type="button" className="mk-btn" onClick={() => pickRef.current?.click()}>{c.pick}</button>
              <button type="button" className="mk-btn ghost mk-phone-only-inline" onClick={() => camRef.current?.click()}>{c.take}</button>
            </div>
            <span className="mk-count">{c.drop} · {c.formats}</span>
            {err && <p className="mk-cam-err static" role="alert" data-image-error="1">{err}</p>}
          </div>
        ) : null}
        {!prep && <RfqAd lang={lang} c={c} />}
        {prep && (
          <>
            <div className="mk-imgq" data-image-query="1">
              <img src={prep.dataUrl} alt={c.yourPhoto} />
              <div style={{ minWidth: 0 }}>
                <h1 style={{ margin: 0, fontSize: 20 }}>{c.title}</h1>
                <p className="mk-count" data-image-status={state} style={{ margin: '4px 0 8px' }}>
                  {busy ? c.searching : state === 'done' && !items.length ? c.none : ''}
                </p>
                <button type="button" className="mk-btn ghost" style={{ height: 36 }} onClick={() => pickRef.current?.click()}>{c.change}</button>
                {/* A failed photo search says so only when nothing else was found. */}
                {err && !items.length && <p className="mk-cam-err static" role="alert" data-image-error="1">{err}</p>}
              </div>
            </div>
            <RfqAd lang={lang} c={c} />
            {state === 'signin' && (
              <div data-image-signin="1" style={{ marginTop: 18, textAlign: 'center' }}>
                <p className="mk-empty" style={{ marginBottom: 10 }}>{c.signIn}</p>
                <button type="button" className="mk-btn" onClick={() => nav(`/login?next=${encodeURIComponent('/market/image-search')}`)}>{c.signInBtn}</button>
              </div>
            )}
            {state !== 'signin' && (
              <>
                <div className="mk-pills" data-image-sorts="1">
                  {SORTS.map((k) => <button key={k} type="button" className={`mk-pill${sort === k ? ' on' : ''}`} data-image-sort={k} onClick={() => setSort(k)}>{c.sorts[k]}</button>)}
                </div>
                <div className="mk-pills">
                  <span className="mk-count">{attributeLabel('private_label', lang === 'ar' ? 'ar' : 'en')}</span>
                  {PL_VALUES.map((v) => <button key={v} type="button" className={`mk-pill${pl.includes(v) ? ' gold' : ''}`} data-image-pl={v} onClick={() => togglePl(v)}>{pl.includes(v) ? '✓ ' : ''}{attributeValueLabel('private_label', v, lang === 'ar' ? 'ar' : 'en')}</button>)}
                </div>

                {items.length > 0 && (
                  <section className="mk-sec" data-image-results={items.length}>
                    <div className="mk-sec-head"><div><h2>{c.similar}</h2><p>{c.note}</p></div></div>
                    <div className="mk-grid cols-4">
                      {items.map((it) => (it.own
                        ? <MarketProductCard key={it.key} p={it.own} lang={lang} user={user} exchangeRates={exchangeRates} />
                        : <SourceCard key={it.key} r={it.src} lang={lang} exchangeRates={exchangeRates} />))}
                    </div>
                  </section>
                )}
                {busy && <div className="mk-grid cols-4" style={{ marginTop: 16 }} data-image-busy="1">{Array.from({ length: 4 }, (_, i) => <div key={i} className="mk-skel" style={{ aspectRatio: '3 / 4' }} />)}</div>}
                {!busy && state === 'done' && items.length === 0 && (
                  <div data-image-none="1" style={{ marginTop: 18 }}>
                    <p className="mk-empty" style={{ marginBottom: 8 }}>{c.none} {c.noneSub}</p>
                    <ServicesPanel lang={lang} />
                  </div>
                )}
                {!busy && own.state === 'done' && own.total > own.rows.length && (
                  <div style={{ textAlign: 'center', marginTop: 16 }}><button type="button" className="mk-btn ghost" onClick={seeAll}>{c.seeAll}</button></div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </MarketShell>
  );
}

/** The advert for quote requests (owner, 2026-10-25) — the store's dark card, opening a direct RFQ. */
function RfqAd({ lang, c }) {
  const nav = useNavigate();
  return (
    <button type="button" className="mk-managed-card" data-image-rfq-ad="1" onClick={() => SERVICE_ROUTES.rfq(nav)}>
      <span><b>{c.ad[0]}</b><span>{c.ad[1]}</span></span>
      <span className="mk-managed-go" aria-hidden="true">{lang === 'ar' ? '←' : '→'}</span>
    </button>
  );
}
